<template>
  <section class="public">

    <header class="public__nav" :class="{ 'public__nav--scrolled': navScrolled }">
      <router-link to="/welcome" class="brand">
        <img :src="logoUrl" alt="Logo Saineo" class="brand__mark" />
        <span class="brand__name">saineo</span>
      </router-link>
      <nav class="navlinks">
        <router-link to="/partenaires" class="navlinks__item">Professionnels</router-link>
        <router-link to="/faq" class="navlinks__item">FAQ</router-link>
        <router-link to="/contact" class="navlinks__item">Contact</router-link>
        <button class="navlinks__item" @click="$router.push('/login')">Se connecter</button>
      </nav>
      <div class="nav-actions">
        <button class="btn btn--solid" @click="$router.push('/register')">Essai gratuit</button>
      </div>
      <button class="hamburger" :class="{ 'hamburger--open': mobileMenuOpen }" @click="mobileMenuOpen = !mobileMenuOpen" aria-label="Menu">
        <span></span>
        <span></span>
        <span></span>
      </button>
    </header>

    <div class="mobile-menu" :class="{ 'mobile-menu--open': mobileMenuOpen }">
      <router-link to="/partenaires" class="mobile-menu__item" @click="mobileMenuOpen = false">Professionnels</router-link>
      <router-link to="/faq" class="mobile-menu__item" @click="mobileMenuOpen = false">FAQ</router-link>
      <router-link to="/contact" class="mobile-menu__item" @click="mobileMenuOpen = false">Contact</router-link>
      <button class="mobile-menu__item" @click="$router.push('/login'); mobileMenuOpen = false">Se connecter</button>
      <hr class="mobile-menu__divider" />
      <button class="btn btn--solid mobile-menu__cta" @click="$router.push('/register'); mobileMenuOpen = false">Essai gratuit</button>
    </div>

    <main class="public__main">
      <div class="public__content">
        <slot />
      </div>
    </main>

  </section>
</template>

<script>
import logoUrl from '@/../assets/logo.png';
import { ref } from 'vue';

export default {
  name: 'PublicLayout',
  setup() {
    const mobileMenuOpen = ref(false);
    const navScrolled = ref(false);

    const handleNavScroll = () => {
      navScrolled.value = window.scrollY > 40;
    };
    window.addEventListener('scroll', handleNavScroll, { passive: true });

    return { mobileMenuOpen, logoUrl, navScrolled };
  },
};
</script>

<style scoped>
  .public {
    --public-ink: #1A1615;
    --public-muted: #4c5562;
    --public-accent: #1A1615;
    --public-inline: clamp(20px, 6vw, 80px);
    min-height: 100vh;
    padding: 0 0 80px 0;
    background: #ffffff;
    color: var(--public-ink);
    font-family: "Open Runde", "Montserrat", "Segoe UI", sans-serif;
    display: flex;
    flex-direction: column;
    position: relative;
  }

  @font-face {
    font-family: "Open Runde";
    src: url("../../../assets/OpenRunde-Regular.otf") format("opentype");
    font-weight: 400; font-style: normal; font-display: swap;
  }
  @font-face {
    font-family: "Open Runde";
    src: url("../../../assets/OpenRunde-Semibold.otf") format("opentype");
    font-weight: 600; font-style: normal; font-display: swap;
  }
  @font-face {
    font-family: "Open Runde";
    src: url("../../../assets/OpenRunde-Bold.otf") format("opentype");
    font-weight: 700; font-style: normal; font-display: swap;
  }

  /* ===== NAV ===== */
  .public__nav {
    position: fixed;
    top: 24px;
    left: 0;
    right: 0;
    width: calc(100% - 80px);
    max-width: 960px;
    margin: 0 auto;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding: 0;
    box-sizing: border-box;
    transition: background 0.4s ease, box-shadow 0.4s ease, padding 0.4s ease, border-radius 0.4s ease;
  }

  .public__nav--scrolled {
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    box-shadow: 0 4px 24px rgba(20, 28, 40, 0.10);
    border-radius: 999px;
    padding: 8px 20px;
    border: 1px solid rgba(255, 255, 255, 0.5);
    transition: background 0.4s ease, box-shadow 0.4s ease, padding 0.4s ease, border-radius 0.4s ease;
  }

  /* ===== BRAND ===== */
  .brand {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 600;
    letter-spacing: -0.02em;
    flex-shrink: 0;
    text-decoration: none;
    color: var(--public-ink);
  }

  .brand__mark { width: 38px; height: 38px; object-fit: contain; display: block; }
  .brand__name { font-size: 20px; text-transform: lowercase; }

  /* ===== NAV LINKS ===== */
  .navlinks {
    display: flex;
    gap: 24px;
    align-items: center;
  }

  .navlinks__item {
    color: #1A1615;
    font-size: 16px;
    font-weight: 400;
    font-family: "Open Runde", "Montserrat", "Segoe UI", sans-serif;
    text-decoration: none;
    opacity: 1;
    transition: opacity 0.2s ease;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
  }

  .navlinks__item:hover { opacity: 0.5; }
  .navlinks__item.router-link-active { opacity: 0.5; }

  .nav-actions {
    display: flex;
    gap: 16px;
    align-items: center;
    flex-shrink: 0;
  }

  /* ===== BUTTONS ===== */
  .btn {
    border: none;
    border-radius: 999px;
    padding: 10px 22px;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.2s ease, opacity 0.2s ease;
    font-family: inherit;
  }

  .btn--solid { background: #1A1615; color: #ffffff; }
  .btn--solid:hover { transform: translateY(-2px); opacity: 0.85; }

  /* ===== HAMBURGER ===== */
  .hamburger {
    display: none;
    flex-direction: column;
    gap: 5px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 6px;
    flex-shrink: 0;
  }

  .hamburger span {
    display: block;
    width: 22px;
    height: 2px;
    background: #1A1615;
    border-radius: 2px;
    transition: transform 0.3s ease, opacity 0.3s ease;
  }

  .hamburger--open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
  .hamburger--open span:nth-child(2) { opacity: 0; }
  .hamburger--open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

  /* ===== MOBILE MENU ===== */
  .mobile-menu {
    display: none;
    position: sticky;
    top: 80px;
    left: 0;
    right: 0;
    width: calc(100% - 40px);
    max-width: 500px;
    margin-inline: auto;
    flex-direction: column;
    gap: 4px;
    background: rgba(255, 255, 255, 0.97);
    backdrop-filter: blur(14px);
    border-radius: 20px;
    padding: 16px;
    box-shadow: 0 8px 32px rgba(20, 28, 40, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.6);
    z-index: 999;
  }

  .mobile-menu--open { display: flex; }

  .mobile-menu__item {
    background: none;
    border: none;
    text-align: left;
    padding: 12px 8px;
    font-size: 1rem;
    font-weight: 400;
    color: #1A1615;
    cursor: pointer;
    border-radius: 10px;
    transition: background 0.2s;
    text-decoration: none;
    display: block;
    font-family: inherit;
  }

  .mobile-menu__item:hover { background: rgba(0,0,0,0.05); }

  .mobile-menu__divider {
    border: none;
    border-top: 1px solid rgba(0,0,0,0.08);
    margin: 4px 0;
  }

  .mobile-menu__cta { width: 100%; margin-top: 4px; }

  /* ===== MAIN CONTENT ===== */
  .public__main {
    display: flex;
    justify-content: center;
    padding: clamp(100px, 12vw, 130px) var(--public-inline) 0;
  }

  .public__content {
    width: 100%;
    max-width: 900px;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  :deep(.public__title) {
    margin: 0;
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 800;
    letter-spacing: -0.02em;
    color: #1A1615;
  }

  :deep(.public__lead) {
    margin: 0;
    color: var(--public-muted);
    font-size: clamp(1rem, 1.4vw, 1.15rem);
    line-height: 1.7;
  }

  :deep(.public__text) {
    margin: 0;
    color: #2a2f36;
    line-height: 1.7;
    font-size: 1rem;
  }

  :deep(.public__section-title) {
    margin: 18px 0 6px;
    font-size: 1.1rem;
    font-weight: 700;
  }

  :deep(.public__list) {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
    color: #2a2f36;
    font-size: 1rem;
  }

  :deep(.public__list li) {
    position: relative;
    padding-left: 18px;
  }

  :deep(.public__list li::before) {
    content: "•";
    position: absolute;
    left: 0;
    color: #1A1615;
  }

  /* ===== RESPONSIVE ===== */
  @media (max-width: 768px) {
    .hamburger { display: flex; }
    .navlinks { display: none; }
    .nav-actions { display: none; }
    .public__nav { width: calc(100% - 40px); padding: 16px 0; }
  }

  @media (max-width: 480px) {
    .public__nav { width: calc(100% - 32px); }
  }
</style>
